import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Clock } from 'lucide-react';

const TimePicker = React.forwardRef(({
  label,
  value,
  onChange,
  name,
  required,
  className = '',
  fullWidth = true,
  error,
  disabled,
  size = 'md',
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const containerRef = useRef(null);
  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const [popupStyle, setPopupStyle] = useState({});

  // Parse "HH:mm" (24h) to { hh: 1-12, mm: 0-59, ampm: 'AM'|'PM' }
  const parseTime = (val) => {
    if (!val) return { hh: 12, mm: 0, ampm: 'AM' };
    const [hStr, mStr] = val.split(':');
    let h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return { hh: h, mm: m, ampm };
  };

  const timeObj = parseTime(value);

  // Convert { hh, mm, ampm } to "HH:mm" (24h)
  const to24h = (hh, mm, ampm) => {
    let h24 = hh;
    if (ampm === 'PM' && hh < 12) h24 += 12;
    if (ampm === 'AM' && hh === 12) h24 = 0;
    return `${String(h24).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };

  const handleUpdate = (newHh, newMm, newAmpm) => {
    const newVal = to24h(newHh, newMm, newAmpm);
    if (onChange) onChange({ target: { name, value: newVal } });
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inPopup = popupRef.current?.contains(e.target);
      if (!inTrigger && !inPopup) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate fixed popup position
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const POPUP_W = 160;
      const POPUP_H = 220;
      const GAP = 6;

      let top = rect.bottom + GAP;
      let left = rect.left;

      if (left + POPUP_W > vw - 8) left = vw - POPUP_W - 8;
      if (left < 8) left = 8;

      let finalTop = top;
      let finalBottom = 'auto';
      if (top + POPUP_H > vh - 8) {
        const flipped = rect.top - POPUP_H - GAP;
        if (flipped >= 8) {
          finalTop = 'auto';
          finalBottom = vh - rect.top + GAP;
        }
      }

      setPopupStyle({ top: finalTop, bottom: finalBottom, left, width: POPUP_W });
    };

    const handleScroll = (e) => {
      if (popupRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };

    updatePosition();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Scroll lists to selected values on open
  useEffect(() => {
    if (isOpen) {
      // Use setTimeout to ensure DOM is ready inside the portal
      setTimeout(() => {
        if (hoursRef.current) {
          const btn = hoursRef.current.querySelector('[data-selected="true"]');
          if (btn) hoursRef.current.scrollTop = btn.offsetTop - hoursRef.current.clientHeight / 2 + btn.clientHeight / 2;
        }
        if (minutesRef.current) {
          const btn = minutesRef.current.querySelector('[data-selected="true"]');
          if (btn) minutesRef.current.scrollTop = btn.offsetTop - minutesRef.current.clientHeight / 2 + btn.clientHeight / 2;
        }
      }, 10);
    }
  }, [isOpen]);

  const displayValue = value
    ? `${String(timeObj.hh).padStart(2, '0')}:${String(timeObj.mm).padStart(2, '0')} ${timeObj.ampm}`
    : '--:--';

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const ampms = ['AM', 'PM'];

  return (
    <div className={`flex flex-col gap-1.5 ${fullWidth ? 'w-full' : ''} ${className}`} ref={containerRef}>
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      {/* Trigger button */}
      <div className="relative group">
        <div
          ref={triggerRef}
          onClick={() => !disabled && setIsOpen(o => !o)}
          className={[
            'w-full px-3 bg-white hover:bg-gray-50 border hover:border-gray-300',
            'rounded-lg shadow-sm text-sm transition-colors duration-200 ease-in-out',
            'cursor-pointer flex items-center justify-between',
            size === 'sm' ? 'min-h-[32px] py-1' : 'min-h-[38px] py-2',
            disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500' : 'text-gray-900',
            error ? 'border-red-500' : 'border-gray-200',
            isOpen && !disabled ? 'ring-2 ring-blue-500/20 border-blue-500' : '',
          ].join(' ')}
        >
          <span className={value ? 'text-gray-900 font-medium text-sm' : 'text-gray-400 text-sm'}>
            {displayValue}
          </span>
          <Clock size={16} className={`shrink-0 transition-colors ${isOpen ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
        </div>
      </div>

      {error && <span className="text-xs font-semibold text-red-500 mt-0.5">{error}</span>}

      {/* Popup */}
      {isOpen && !disabled && ReactDOM.createPortal(
        <div
          ref={popupRef}
          style={{
            position: 'fixed',
            top: popupStyle.top,
            bottom: popupStyle.bottom,
            left: popupStyle.left,
            width: popupStyle.width || 160,
            zIndex: 9999,
          }}
          className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-scale-in"
        >
          <div className="flex h-[200px]">
            {/* Hours */}
            <div ref={hoursRef} className="flex-1 overflow-y-auto border-r border-gray-100 py-1 scroll-smooth relative" style={{ scrollbarWidth: 'none' }}>
              <div className="h-[80px]" /> {/* Padding top */}
              {hours.map(h => (
                <button
                  key={`h-${h}`}
                  type="button"
                  data-selected={h === timeObj.hh}
                  onClick={() => handleUpdate(h, timeObj.mm, timeObj.ampm)}
                  className={`block w-full text-center py-1.5 text-xs transition-colors ${h === timeObj.hh ? 'bg-blue-600 text-white font-bold' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  {String(h).padStart(2, '0')}
                </button>
              ))}
              <div className="h-[80px]" /> {/* Padding bottom */}
            </div>
            {/* Minutes */}
            <div ref={minutesRef} className="flex-1 overflow-y-auto border-r border-gray-100 py-1 scroll-smooth relative" style={{ scrollbarWidth: 'none' }}>
              <div className="h-[80px]" /> {/* Padding top */}
              {minutes.map(m => (
                <button
                  key={`m-${m}`}
                  type="button"
                  data-selected={m === timeObj.mm}
                  onClick={() => handleUpdate(timeObj.hh, m, timeObj.ampm)}
                  className={`block w-full text-center py-1.5 text-xs transition-colors ${m === timeObj.mm ? 'bg-blue-600 text-white font-bold' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
              <div className="h-[80px]" /> {/* Padding bottom */}
            </div>
            {/* AM/PM */}
            <div className="flex-1 overflow-y-auto py-1">
              {ampms.map(ap => (
                <button
                  key={ap}
                  type="button"
                  onClick={() => handleUpdate(timeObj.hh, timeObj.mm, ap)}
                  className={`block w-full text-center py-1.5 text-xs transition-colors ${ap === timeObj.ampm ? 'bg-blue-600 text-white font-bold' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  {ap}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

TimePicker.displayName = 'TimePicker';
export default TimePicker;
